import * as React from "react";
import { type LucideIcon } from "lucide-react";
import { mergeClassNames } from "./class-names";

export interface SegmentedControlOption<Value extends string> {
  readonly value: Value;
  readonly label: React.ReactNode;
  readonly icon?: LucideIcon;
  readonly disabled?: boolean;
  /** Explains a disabled segment on hover, e.g. why a view is unavailable. */
  readonly title?: string;
}

export interface SegmentedControlProps<Value extends string> {
  /** Accessible name for the group of choices. */
  readonly label: string;
  readonly value: Value;
  readonly options: readonly SegmentedControlOption<Value>[];
  readonly onChange: (value: Value) => void;
  readonly className?: string | undefined;
}

/**
 * One mutually exclusive choice between a small, fixed set of modes — a view
 * switch, a capture mode, an allocation basis. The selected segment is
 * carried by a raised surface and weight as well as colour.
 */
export function SegmentedControl<Value extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: SegmentedControlProps<Value>) {
  return (
    <div
      role="group"
      aria-label={label}
      data-slot="segmented-control"
      className={mergeClassNames(
        "inline-flex w-fit max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border-subtle bg-subtle p-0.5",
        className,
      )}
    >
      {options.map((option) => {
        const Icon = option.icon;
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            disabled={option.disabled}
            {...(option.title === undefined ? {} : { title: option.title })}
            onClick={() => onChange(option.value)}
            className={mergeClassNames(
              "text-body inline-flex min-h-11 shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-sm px-3 font-semibold transition-[background-color,color,box-shadow] duration-[var(--motion-duration-fast)] ease-[var(--motion-easing-standard)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50",
              isSelected
                ? "bg-surface text-primary shadow-[var(--elevation-1)]"
                : "text-secondary hover:text-primary",
            )}
          >
            {Icon === undefined ? null : (
              <Icon className="size-4 shrink-0" aria-hidden="true" />
            )}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
